import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  TrendingUp, AlertTriangle, RefreshCw, ChevronDown, ChevronUp,
  Loader2, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { getLwpForecast } from '../api';

const RISK_STYLES = {
  high:      { badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  medium:    { badge: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  low:       { badge: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  probation: { badge: 'bg-violet-100 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  ok:        { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
};

export default function LwpForecastCard({ defaultOpen = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getLwpForecast();
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load LWP forecast');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = !data ? [] : (
    filter === 'all'
      ? data.forecasts
      : data.forecasts.filter((f) => f.risk_level === filter)
  );

  return (
    <Card className="border-orange-200" data-testid="lwp-forecast-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            className="flex items-center gap-2 text-left flex-1 hover:opacity-80 transition"
            onClick={() => setOpen((v) => !v)}
            data-testid="lwp-forecast-toggle"
          >
            <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
              <TrendingUp size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-slate-800">
                  Year-end LWP Forecast {data?.year ? `(${data.year})` : ''}
                </h3>
                {data && (
                  <Badge
                    className={
                      data.high_risk_count > 0
                        ? RISK_STYLES.high.badge
                        : data.medium_risk_count > 0
                        ? RISK_STYLES.medium.badge
                        : data.at_risk_count > 0
                        ? RISK_STYLES.low.badge
                        : RISK_STYLES.ok.badge
                    }
                    data-testid="lwp-at-risk-count"
                  >
                    {data.at_risk_count} at risk
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Projects who'll need LWP by Dec 31 based on current leave pace.
              </p>
            </div>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {open && (
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="lwp-refresh-btn">
              <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>

        {open && (
          <div className="mt-4 space-y-3">
            {loading && !data && (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <Loader2 size={16} className="mr-2 animate-spin" /> Scanning employees…
              </div>
            )}

            {data && (
              <>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  {[
                    ['high', 'High', data.high_risk_count],
                    ['medium', 'Medium', data.medium_risk_count],
                    ['low', 'Low', data.low_risk_count],
                    ['probation', 'Probation', data.probation_count],
                  ].map(([k, label, count]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setFilter(filter === k ? 'all' : k)}
                      className={`rounded-lg border p-2 transition hover:scale-105 ${
                        filter === k ? RISK_STYLES[k].badge : 'border-slate-200 bg-slate-50'
                      }`}
                      data-testid={`lwp-filter-${k}`}
                    >
                      <div className="text-lg font-bold">{count}</div>
                      <div className="text-[10px] uppercase tracking-wide">{label}</div>
                    </button>
                  ))}
                </div>

                {filter !== 'all' && (
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    Showing {filter} risk only.
                    <button
                      type="button"
                      onClick={() => setFilter('all')}
                      className="underline text-indigo-600 hover:text-indigo-700"
                    >
                      Clear filter
                    </button>
                  </div>
                )}

                {filtered.length === 0 ? (
                  <div className="text-center py-6 text-emerald-700 text-sm">
                    🎉 No employees in the {filter !== 'all' ? `${filter} risk` : 'forecast'} list — everyone's leave usage is on track.
                  </div>
                ) : (
                  <div className="max-h-[450px] overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Employee</th>
                          <th className="text-left p-2 font-medium">Department</th>
                          <th className="text-right p-2 font-medium">Paid used</th>
                          <th className="text-right p-2 font-medium">LWP used</th>
                          <th className="text-right p-2 font-medium">Projected LWP</th>
                          <th className="text-left p-2 font-medium">Risk</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((f, idx) => (
                          <tr key={f.employee_id || idx} className="border-t hover:bg-slate-50" data-testid={`lwp-row-${idx}`}>
                            <td className="p-2">
                              <div className="font-medium text-slate-900">{f.employee_name}</div>
                              {f.employee_code && (
                                <div className="text-[10px] text-slate-400">{f.employee_code}</div>
                              )}
                            </td>
                            <td className="p-2 text-slate-600">{f.department || '—'}</td>
                            <td className="p-2 text-right">{f.paid_used_so_far}</td>
                            <td className="p-2 text-right">{f.unpaid_used_so_far}</td>
                            <td className="p-2 text-right font-bold text-red-600">
                              {f.projected_lwp_total}
                            </td>
                            <td className="p-2">
                              <Badge className={`border ${RISK_STYLES[f.risk_level]?.badge || ''} gap-1 text-[10px]`}>
                                {f.risk_level === 'high' && <AlertTriangle size={10} />}
                                {f.risk_level === 'probation' && <Calendar size={10} />}
                                {f.risk_level.toUpperCase()}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <p className="text-[10px] text-slate-400 text-center pt-1">
                  Forecast as of {data.as_of} — scanned {data.total_employees_scanned} active employees, threshold {data.threshold} day(s).
                </p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
